import type Konva from "konva";
import type { Clip, Track } from "./EditorStore";
import type { FxBlendMode, FxBlock } from "./fxTypes";
import type { BodyTrackingState } from "./fxBodyTracking";
import { FX_BODY_EFFECT_TYPES, sortFxBlocksByTrackOrder } from "./fxTypes";
import { resolveFxGpuModuleId, resolveFxRenderPath, resolveFxRuntimeProfile } from "./fxEffectCatalog";
import {
  FX_DEBUG,
  logFxBlockActivityTransition,
  logFxRouteStatusTransition,
  cleanupStaleActivityEntries,
} from "./fxRuntime.activity";
import {
  FX_ACTIVE_EPS,
  getFxClipsByIdCached,
  dedupeFxBlocksById,
  getTargetElementIds,
  isPlayheadWithinFxWindow,
  packetEffectTypeIsBackgroundBlur,
  packetIsBackgroundBlur,
  packetWantsSegmentation,
  selectBackgroundBlurPacketForOverlay,
} from "./fxRuntime.targeting";
import {
  FX_EPSILON,
  TWO_PI,
  FX_GPU_MODULE_TUNING,
  type FxNodeModifier,
  type FxBlockFrameEvaluation,
  resolveModulationFactor,
  evaluateFxBlockAtPlayhead,
  cleanupStaleModulationState,
} from "./fxRuntime.evaluation";
import { clamp01, clamp, toFinite, hash01, hashSigned } from "./fxRuntime.shared";

export type FxQualityTier = "draft" | "balanced" | "final";

export type FxRenderPacket = {
  fxBlockId: string;
  effectType: string;
  renderPath: "cpu-transform" | "gpu-module";
  category: FxBlock["category"];
  blendMode: FxBlock["blendMode"];
  opacity: number;
  mask: FxBlock["mask"];
  layers: FxBlock["layers"];
  seed: number;
  progress: number;
  qualityTier: FxQualityTier;
  modulation: FxBlock["modulation"];
  params: Record<string, number>;
  durationSec: number;
  gpuModuleId?: string | null;
};

export type FxMaskClipPath = (ctx: CanvasRenderingContext2D) => void;

export type FxAudioFeatures = {
  low: number;
  mid: number;
  high: number;
  full: number;
  onset: number;
  transient: number;
  beat: number;
  tempoBpm: number;
};

// computeFxRenderPackets moved to fxRuntime.packets.ts (T8).
// Re-exported below for consumer stability.
export { computeFxRenderPackets } from "./fxRuntime.packets";

export type FxNodeBaseState = {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  opacity: number;
  globalCompositeOperation: GlobalCompositeOperation;
};

const FX_BLEND_TO_COMPOSITE: Record<FxBlendMode, GlobalCompositeOperation> = {
  normal: "source-over",
  multiply: "multiply",
  screen: "screen",
  lighten: "lighten",
  overlay: "overlay",
  "soft-light": "soft-light",
  "hard-light": "hard-light",
  "color-dodge": "color-dodge",
  "color-burn": "color-burn",
  add: "lighter",
  difference: "difference",
  exclusion: "exclusion",
};

export function resolveFxBlendComposite(blendMode: FxBlendMode): GlobalCompositeOperation {
  return FX_BLEND_TO_COMPOSITE[blendMode] ?? "source-over";
}

const FX_BODY_EFFECT_SET = new Set<string>([...FX_BODY_EFFECT_TYPES, "background-blur"]);

export function packetNeedsBodyTracking(packet: Pick<FxRenderPacket, "category" | "effectType">): boolean {
  const fxType = resolveFxRuntimeProfile(packet.effectType);
  if (fxType === "background-blur-only" || fxType === "background-blur") {
    return false;
  }
  if (packet.category === "body") return true;
  return FX_BODY_EFFECT_SET.has(fxType);
}

// packetIsBackgroundBlur, packetWantsSegmentation,
// selectBackgroundBlurPacketForOverlay moved to fxRuntime.targeting.ts (T6).

// Re-export externally-consumed targeting symbols so consumers importing
// from "./fxRuntime" are unaffected.
export {
  packetIsBackgroundBlur,
  packetWantsSegmentation,
  selectBackgroundBlurPacketForOverlay,
} from "./fxRuntime.targeting";

export function resolveFxMaskClipPath(args: {
  packets: FxRenderPacket[] | undefined;
  width: number;
  height: number;
}): FxMaskClipPath | null {
  const { packets, width, height } = args;
  if (!packets || packets.length === 0) return null;
  if (!(width > 0) || !(height > 0)) return null;

  let packet: FxRenderPacket | null = null;
  for (let i = packets.length - 1; i >= 0; i -= 1) {
    const candidate = packets[i];
    if (packetIsBackgroundBlur(candidate)) continue;
    const mask = candidate?.mask;
    if (!mask || mask.type === "none") continue;
    if (toFinite(mask.opacity, 1) <= 0.001) continue;
    packet = candidate;
    break;
  }
  if (!packet) return null;

  const mask = packet.mask;
  const params = packet.params || {};
  const expandPx = clamp(toFinite(mask.expandPx, 0), -500, 500);
  const invert = !!mask.invert;
  const type = mask.type;

  const rect = resolveMaskRect({
    params,
    width,
    height,
    expandPx,
  });

  const drawBaseMaskPath = (ctx: CanvasRenderingContext2D, reverse: boolean = false) => {
    if (type === "rect") {
      drawRectPath(ctx, rect.x, rect.y, rect.width, rect.height, reverse);
      return;
    }

    if (type === "ellipse") {
      drawEllipsePath(ctx, rect, reverse);
      return;
    }

    if (type === "linear-gradient") {
      drawLinearGradientMaskPath(ctx, params, width, height, reverse);
      return;
    }

    if (type === "radial-gradient") {
      drawRadialMaskPath(ctx, params, rect, width, height, reverse);
      return;
    }

    if (type === "free-draw") {
      const drew = drawFreeDrawMaskPath(ctx, {
        mask,
        width,
        height,
        reverse,
      });
      if (drew) return;
    }

    // Graceful fallback if free-draw has no valid paths.
    drawEllipsePath(ctx, rect, reverse);
  };

  if (!invert) {
    return (ctx: CanvasRenderingContext2D) => {
      ctx.beginPath();
      drawBaseMaskPath(ctx, false);
      ctx.closePath();
    };
  }

  return (ctx: CanvasRenderingContext2D) => {
    // Use opposite winding for inner path to emulate inverse mask with non-zero rule.
    ctx.beginPath();
    drawRectPath(ctx, 0, 0, width, height, false);
    drawBaseMaskPath(ctx, true);
    ctx.closePath();
  };
}

// FxNodeModifier type, FX_EPSILON, TWO_PI, MODULATION constants
// moved to fxRuntime.evaluation.ts (T7 of the file-splits plan).

// FX_GPU_MODULE_TUNING, FxModulationRuntimeState, modulationRuntimeStateByFxBlockId
// moved to fxRuntime.evaluation.ts (T7 of the file-splits plan).

export function captureFxNodeBaseState(node: Konva.Node): FxNodeBaseState {
  return {
    x: node.x(),
    y: node.y(),
    scaleX: node.scaleX(),
    scaleY: node.scaleY(),
    rotation: node.rotation(),
    opacity: node.opacity(),
    globalCompositeOperation: getNodeCompositeOperation(node),
  };
}

export function restoreFxNodeState(args: {
  node: Konva.Node;
  baseState: FxNodeBaseState;
}): boolean {
  const { node, baseState } = args;
  let changed = false;

  changed = assignIfChanged(node.x(), baseState.x, (value) => node.x(value)) || changed;
  changed = assignIfChanged(node.y(), baseState.y, (value) => node.y(value)) || changed;
  changed = assignIfChanged(node.scaleX(), baseState.scaleX, (value) => node.scaleX(value)) || changed;
  changed = assignIfChanged(node.scaleY(), baseState.scaleY, (value) => node.scaleY(value)) || changed;
  changed = assignIfChanged(node.rotation(), baseState.rotation, (value) => node.rotation(value)) || changed;
  changed = assignIfChanged(node.opacity(), clamp01(baseState.opacity), (value) => node.opacity(value)) || changed;
  changed = setNodeCompositeOperation(node, baseState.globalCompositeOperation) || changed;

  return changed;
}

export function applyFxPacketsToNode(args: {
  node: Konva.Node;
  packets: FxRenderPacket[];
  playhead: number;
  baseState: FxNodeBaseState;
  audioFeatures?: FxAudioFeatures;
  bodyTrackingState?: BodyTrackingState | null;
}): boolean {
  const { node, packets, playhead, baseState, audioFeatures, bodyTrackingState } = args;
  if (!packets.length) return false;

  let offsetX = 0;
  let offsetY = 0;
  let scaleMultiplier = 1;
  let rotationOffset = 0;
  let opacityMultiplier = 1;
  let nextComposite = baseState.globalCompositeOperation;

  for (const packet of packets) {
    if (packetIsBackgroundBlur(packet)) {
      logFxRouteStatusTransition(packet, playhead, "background-compositor");
      continue;
    }
    if (packet.renderPath === "gpu-module") {
      logFxRouteStatusTransition(packet, playhead, "gpu-image-pipeline");
      continue;
    }
    // Early culling: skip negligible-opacity packets entirely
    const packetOpacity = clamp01(toFinite(packet.opacity, 1));
    if (packetOpacity < FX_EPSILON) continue;

    logFxRouteStatusTransition(packet, playhead, "cpu-transform");
    const mod = evaluatePacketModifier(packet, playhead, audioFeatures, bodyTrackingState);
    offsetX += mod.offsetX;
    offsetY += mod.offsetY;
    scaleMultiplier *= mod.scaleMultiplier;
    rotationOffset += mod.rotationOffset;
    opacityMultiplier *= mod.opacityMultiplier;
    opacityMultiplier *= clamp01(toFinite(packet.mask?.opacity, 1));
    if (mod.globalCompositeOperation) nextComposite = mod.globalCompositeOperation;
  }

  const nextScale = Math.max(0.05, scaleMultiplier);
  const nextX = baseState.x + offsetX;
  const nextY = baseState.y + offsetY;
  const nextScaleX = baseState.scaleX * nextScale;
  const nextScaleY = baseState.scaleY * nextScale;
  const nextRotation = baseState.rotation + rotationOffset;
  const nextOpacity = clamp01(baseState.opacity * opacityMultiplier);

  let changed = false;
  changed = assignIfChanged(node.x(), nextX, (value) => node.x(value)) || changed;
  changed = assignIfChanged(node.y(), nextY, (value) => node.y(value)) || changed;
  changed = assignIfChanged(node.scaleX(), nextScaleX, (value) => node.scaleX(value)) || changed;
  changed = assignIfChanged(node.scaleY(), nextScaleY, (value) => node.scaleY(value)) || changed;
  changed = assignIfChanged(node.rotation(), nextRotation, (value) => node.rotation(value)) || changed;
  changed = assignIfChanged(node.opacity(), nextOpacity, (value) => node.opacity(value)) || changed;
  changed = setNodeCompositeOperation(node, nextComposite) || changed;
  return changed;
}

function evaluatePacketModifier(
  packet: FxRenderPacket,
  playhead: number,
  audioFeatures?: FxAudioFeatures,
  bodyTrackingState?: BodyTrackingState | null
): FxNodeModifier {
  const modulationFactor = resolveModulationFactor(packet, audioFeatures, playhead);
  const gpuModuleId = packet.gpuModuleId ?? resolveFxGpuModuleId(packet.effectType);
  const gpuTuning = gpuModuleId ? FX_GPU_MODULE_TUNING[gpuModuleId] : null;
  const intensityBase = (toFinite(packet.params.intensity, 1) || 1) * (gpuTuning?.intensity ?? 1);
  const amplitudeBase = (toFinite(packet.params.amplitude, 1) || 1) * (gpuTuning?.amplitude ?? 1);
  const speedBase = (toFinite(packet.params.speed, 1) || 1) * (gpuTuning?.speed ?? 1);
  const intensity = clamp01(intensityBase * clamp01(packet.opacity) * modulationFactor);
  const amplitude = Math.max(0, amplitudeBase);
  const speed = Math.max(0.1, speedBase);
  const seedPhase = (packet.seed % 360) * (Math.PI / 180);
  const phase = packet.progress * TWO_PI * speed + seedPhase;
  const fxType = resolveFxRuntimeProfile(packet.effectType);
  const blendComposite = packet.blendMode !== "normal" ? FX_BLEND_TO_COMPOSITE[packet.blendMode] : undefined;

  if (packetNeedsBodyTracking(packet)) {
    return evaluateBodyPacketModifier({
      fxType,
      phase,
      intensity,
      amplitude,
      blendComposite,
      bodyTrackingState,
    });
  }

  if (fxType === "light-leak-sweep" || fxType === "light-leak" || fxType === "leak") {
    const sweep = Math.sin(phase * 1.3);
    const drift = Math.cos(phase * 0.9);
    const pulse = 0.82 + 0.35 * (0.5 + 0.5 * Math.sin(phase * 2.1));
    return {
      offsetX: sweep * 24 * intensity * amplitude,
      offsetY: drift * 10 * intensity * amplitude,
      scaleMultiplier: 1 + 0.04 * intensity * (0.5 + 0.5 * Math.sin(phase * 1.4)),
      rotationOffset: Math.sin(phase * 0.7) * 1.8 * intensity,
      opacityMultiplier: clamp(pulse, 0.25, 1.35),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "zoom-pulse" || fxType === "pulse") {
    const pulse = Math.sin(phase * 2);
    return {
      offsetX: 0,
      offsetY: 0,
      scaleMultiplier: 1 + 0.1 * intensity * amplitude * pulse,
      rotationOffset: 0,
      opacityMultiplier: 1,
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "shake" || fxType === "handheld") {
    const tick = Math.floor(playhead * 28 * speed);
    const jitterX = hashSigned(packet.seed + tick * 17.113);
    const jitterY = hashSigned(packet.seed + tick * 9.731 + 11.7);
    const jitterR = hashSigned(packet.seed + tick * 5.233 + 23.9);
    return {
      offsetX: jitterX * 10 * intensity * amplitude,
      offsetY: jitterY * 6 * intensity * amplitude,
      scaleMultiplier: 1,
      rotationOffset: jitterR * 1.8 * intensity,
      opacityMultiplier: 1,
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "camera-shake-pro") {
    const tick = Math.floor(playhead * 42 * speed);
    const baseJitterX = hashSigned(packet.seed + tick * 13.713);
    const baseJitterY = hashSigned(packet.seed + tick * 9.127 + 17.2);
    const microJitterX = hashSigned(packet.seed + tick * 31.071 + 4.9);
    const microJitterY = hashSigned(packet.seed + tick * 27.551 + 2.1);
    const rotational = hashSigned(packet.seed + tick * 7.201 + 11.4);
    const envelope = 0.72 + 0.28 * (0.5 + 0.5 * Math.sin(phase * 2.9));
    return {
      offsetX: (baseJitterX * 11 + microJitterX * 3.2) * intensity * amplitude * envelope,
      offsetY: (baseJitterY * 7 + microJitterY * 2.6) * intensity * amplitude * envelope,
      scaleMultiplier: 1 + 0.014 * intensity * envelope,
      rotationOffset: rotational * 2.6 * intensity * envelope,
      opacityMultiplier: clamp(0.9 + 0.08 * intensity, 0.3, 1.2),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "transient-shake") {
    const tick = Math.floor(playhead * 52 * speed);
    const transient = 0.5 + 0.5 * Math.sin(phase * 4.8);
    const jitterX = hashSigned(packet.seed + tick * 19.37);
    const jitterY = hashSigned(packet.seed + tick * 13.21 + 5.3);
    const jitterR = hashSigned(packet.seed + tick * 8.11 + 12.6);
    return {
      offsetX: jitterX * 13 * intensity * amplitude * transient,
      offsetY: jitterY * 9 * intensity * amplitude * transient,
      scaleMultiplier: 1 + 0.018 * intensity * transient,
      rotationOffset: jitterR * 2.1 * intensity * transient,
      opacityMultiplier: clamp(0.9 + 0.14 * intensity * transient, 0.28, 1.2),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "flicker" || fxType === "strobe") {
    const tick = Math.floor(playhead * 24 * speed);
    const flicker = 0.65 + 0.65 * hash01(packet.seed + tick * 13.77);
    return {
      offsetX: 0,
      offsetY: 0,
      scaleMultiplier: 1,
      rotationOffset: 0,
      opacityMultiplier: clamp(flicker, 0.2, 1.4),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "onset-flash") {
    const onset = clamp01(0.5 + 0.5 * Math.sin(phase * 6.6));
    return {
      offsetX: 0,
      offsetY: 0,
      scaleMultiplier: 1 + 0.01 * intensity * onset,
      rotationOffset: 0,
      opacityMultiplier: clamp(0.82 + 0.46 * intensity * onset, 0.2, 1.45),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "bar-flash-overlay") {
    const sweep = (packet.progress * 1.7 + (0.5 + 0.5 * Math.sin(phase * 2.1))) % 1;
    const bar = clamp01(1 - Math.abs((0.1 + sweep * 0.8) - 0.5) / 0.5);
    const pulse = 0.5 + 0.5 * Math.sin(phase * 5.8);
    return {
      offsetX: Math.sin(phase * 1.6) * 4 * intensity * amplitude * pulse,
      offsetY: 0,
      scaleMultiplier: 1 + 0.018 * intensity * pulse,
      rotationOffset: Math.sin(phase * 0.9) * 0.6 * intensity * pulse,
      opacityMultiplier: clamp(0.9 + 0.46 * intensity * pulse * bar, 0.2, 1.44),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "film-gate-jitter") {
    const tick = Math.floor(playhead * 24 * speed);
    const gateX = hashSigned(packet.seed + tick * 6.37);
    const gateY = hashSigned(packet.seed + tick * 8.11 + 7.3);
    const gateR = hashSigned(packet.seed + tick * 3.91 + 13.2);
    const jumpGate = hash01(packet.seed + tick * 2.71 + 17.9) > 0.93 ? 1 : 0;
    return {
      offsetX: gateX * 7 * intensity * amplitude,
      offsetY: gateY * 5 * intensity * amplitude - jumpGate * 8 * intensity,
      scaleMultiplier: 1 + 0.012 * intensity * (0.5 + jumpGate * 0.5),
      rotationOffset: gateR * 1.1 * intensity,
      opacityMultiplier: clamp(0.9 + 0.16 * intensity, 0.3, 1.22),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "data-moshing") {
    const tick = Math.floor(playhead * 20 * speed);
    const gate = hash01(packet.seed + tick * 2.91 + 7.8) > 0.7 ? 1 : 0;
    const jumpX = hashSigned(packet.seed + tick * 8.37);
    const jumpY = hashSigned(packet.seed + tick * 5.12 + 4.3);
    return {
      offsetX: jumpX * 24 * intensity * amplitude * gate,
      offsetY: jumpY * 8 * intensity * amplitude * gate,
      scaleMultiplier: 1 + 0.035 * intensity * gate,
      rotationOffset: hashSigned(packet.seed + tick * 3.01 + 15.9) * 2.4 * intensity * gate,
      opacityMultiplier: clamp(0.82 + 0.34 * gate, 0.2, 1.24),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "screen-crack-impact") {
    const impact = Math.pow(clamp01(1 - packet.progress / 0.24), 2);
    const settle = 0.22 + 0.78 * (1 - clamp01((packet.progress - 0.24) / 0.76));
    const envelope = Math.max(impact, settle * 0.24);
    return {
      offsetX: Math.sin(seedPhase) * 7 * intensity * amplitude * envelope,
      offsetY: Math.cos(seedPhase) * 5 * intensity * amplitude * envelope,
      scaleMultiplier: 1 + 0.045 * intensity * envelope,
      rotationOffset: hashSigned(packet.seed + 91.3) * 1.7 * intensity * envelope,
      opacityMultiplier: clamp(0.9 + 0.22 * intensity * envelope, 0.25, 1.25),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "speed-ramp-blur") {
    const accel = clamp01(packet.progress / 0.22);
    const sustain = 1 - clamp01((packet.progress - 0.62) / 0.38);
    const ramp = Math.max(accel * sustain, 0.14);
    const dirX = Math.sign(Math.sin(seedPhase) || 1);
    return {
      offsetX: dirX * 12 * intensity * amplitude * ramp,
      offsetY: Math.cos(seedPhase) * 4 * intensity * amplitude * ramp,
      scaleMultiplier: 1 + 0.032 * intensity * ramp,
      rotationOffset: dirX * 0.9 * intensity * ramp,
      opacityMultiplier: clamp(0.9 + 0.14 * ramp, 0.24, 1.2),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "flash-white-pro") {
    const flashHead = clamp01(1 - Math.abs(packet.progress - 0.14) / 0.16);
    const flashTail = clamp01(1 - Math.abs(packet.progress - 0.78) / 0.12);
    const flash = Math.max(flashHead, flashTail);
    return {
      offsetX: 0,
      offsetY: 0,
      scaleMultiplier: 1 + 0.018 * intensity * flash,
      rotationOffset: 0,
      opacityMultiplier: clamp(0.9 + 0.42 * intensity * flash, 0.2, 1.45),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "directional-motion-blur-pro") {
    const dir = Math.sin(seedPhase * 0.9) >= 0 ? 1 : -1;
    const wave = 0.5 + 0.5 * Math.sin(phase * 1.6);
    return {
      offsetX: dir * 9 * intensity * amplitude * wave,
      offsetY: Math.cos(seedPhase) * 3 * intensity * amplitude * wave,
      scaleMultiplier: 1 + 0.02 * intensity * wave,
      rotationOffset: dir * 0.7 * intensity * wave,
      opacityMultiplier: clamp(0.9 + 0.12 * intensity * wave, 0.3, 1.2),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "fog-depth-layer") {
    const depthDrift = 0.5 + 0.5 * Math.sin(phase * 0.7);
    return {
      offsetX: Math.sin(phase * 0.5) * 6 * intensity * amplitude,
      offsetY: Math.cos(phase * 0.3) * 2 * intensity * amplitude * depthDrift,
      scaleMultiplier: 1 + 0.014 * intensity * depthDrift,
      rotationOffset: Math.sin(phase * 0.2) * 0.35 * intensity,
      opacityMultiplier: clamp(0.92 + 0.2 * intensity * depthDrift, 0.28, 1.22),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "drift" || fxType === "parallax-drift") {
    return {
      offsetX: Math.sin(phase) * 14 * intensity * amplitude,
      offsetY: Math.cos(phase * 0.7) * 8 * intensity * amplitude,
      scaleMultiplier: 1 + 0.03 * intensity * Math.sin(phase * 0.5),
      rotationOffset: Math.sin(phase * 0.35) * 1.2 * intensity,
      opacityMultiplier: 1,
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "film-burn") {
    const flare = 0.5 + 0.5 * Math.sin(phase * 1.4 + 0.8);
    const streak = 0.5 + 0.5 * Math.sin(phase * 2.3);
    return {
      offsetX: Math.sin(phase * 0.45) * 8 * intensity * amplitude,
      offsetY: -Math.abs(Math.cos(phase * 0.9)) * 10 * intensity * amplitude,
      scaleMultiplier: 1 + 0.075 * intensity * flare,
      rotationOffset: Math.sin(phase * 0.4) * 1.4 * intensity,
      opacityMultiplier: clamp(0.76 + 0.5 * intensity * streak, 0.24, 1.4),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "halation-glow") {
    const glow = 0.5 + 0.5 * Math.sin(phase * 1.25);
    return {
      offsetX: Math.sin(phase * 0.75) * 4 * intensity * amplitude,
      offsetY: Math.cos(phase * 0.55) * 3 * intensity * amplitude,
      scaleMultiplier: 1 + 0.06 * intensity * (0.4 + 0.6 * glow),
      rotationOffset: Math.sin(phase * 0.3) * 0.8 * intensity,
      opacityMultiplier: clamp(0.96 + 0.24 * intensity * glow, 0.3, 1.35),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "kodak-lut-pack") {
    const filmPulse = 0.5 + 0.5 * Math.sin(phase * 0.6);
    return {
      offsetX: Math.sin(phase * 0.24) * 2 * intensity * amplitude,
      offsetY: Math.cos(phase * 0.21) * 1.6 * intensity * amplitude,
      scaleMultiplier: 1 + 0.01 * intensity * filmPulse,
      rotationOffset: Math.sin(phase * 0.14) * 0.25 * intensity,
      opacityMultiplier: clamp(0.96 + 0.12 * intensity * filmPulse, 0.3, 1.2),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "anamorphic-streak") {
    const sweep = Math.sin(phase * 0.95);
    return {
      offsetX: sweep * 10 * intensity * amplitude,
      offsetY: Math.cos(phase * 0.32) * 2 * intensity,
      scaleMultiplier: 1 + 0.018 * intensity * (0.5 + 0.5 * sweep),
      rotationOffset: Math.sin(phase * 0.2) * 0.55 * intensity,
      opacityMultiplier: clamp(0.92 + 0.24 * intensity, 0.3, 1.28),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "cinematic-bloom-threshold") {
    const bloomPulse = 0.5 + 0.5 * Math.sin(phase * 1.05);
    return {
      offsetX: Math.sin(phase * 0.45) * 3 * intensity * amplitude,
      offsetY: Math.cos(phase * 0.42) * 2 * intensity * amplitude,
      scaleMultiplier: 1 + 0.028 * intensity * bloomPulse,
      rotationOffset: Math.sin(phase * 0.22) * 0.45 * intensity,
      opacityMultiplier: clamp(0.94 + 0.2 * intensity * bloomPulse, 0.3, 1.28),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "soft-film-fade") {
    const fadeWave = 0.5 + 0.5 * Math.sin(phase * 0.65);
    return {
      offsetX: Math.sin(phase * 0.3) * 2 * intensity,
      offsetY: Math.cos(phase * 0.28) * 1.5 * intensity,
      scaleMultiplier: 1 + 0.01 * intensity * fadeWave,
      rotationOffset: Math.sin(phase * 0.18) * 0.25 * intensity,
      opacityMultiplier: clamp(0.82 + 0.22 * (1 - fadeWave) * intensity, 0.28, 1.12),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "film-grain") {
    const tick = Math.floor(playhead * 32 * speed);
    const grainX = hashSigned(packet.seed + tick * 5.17);
    const grainY = hashSigned(packet.seed + tick * 11.91 + 2.7);
    return {
      offsetX: grainX * 1.4 * intensity * amplitude,
      offsetY: grainY * 1.1 * intensity * amplitude,
      scaleMultiplier: 1,
      rotationOffset: grainX * 0.25 * intensity,
      opacityMultiplier: clamp(0.9 + 0.18 * intensity * (0.5 + 0.5 * grainY), 0.58, 1.2),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "lens-dust") {
    const tick = Math.floor(playhead * 14 * speed);
    const sparkle = hash01(packet.seed + tick * 19.33);
    return {
      offsetX: Math.sin(phase * 0.5) * 6 * intensity * amplitude,
      offsetY: Math.cos(phase * 0.4) * 4 * intensity * amplitude,
      scaleMultiplier: 1 + 0.02 * intensity * (0.3 + sparkle * 0.7),
      rotationOffset: Math.sin(phase * 0.2) * 0.6 * intensity,
      opacityMultiplier: clamp(0.88 + 0.26 * intensity * sparkle, 0.3, 1.25),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "floating-embers") {
    const driftX = Math.sin(phase * 0.42);
    const rise = 0.5 + 0.5 * Math.sin(phase * 1.2);
    return {
      offsetX: driftX * 8 * intensity * amplitude,
      offsetY: -Math.abs(rise) * 12 * intensity * amplitude,
      scaleMultiplier: 1 + 0.018 * intensity * (0.4 + rise * 0.6),
      rotationOffset: Math.sin(phase * 0.28) * 0.8 * intensity,
      opacityMultiplier: clamp(0.9 + 0.2 * intensity * rise, 0.24, 1.24),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "water-droplets") {
    const sway = Math.sin(phase * 0.55);
    return {
      offsetX: sway * 5 * intensity * amplitude,
      offsetY: Math.cos(phase * 0.34) * 2 * intensity * amplitude,
      scaleMultiplier: 1 + 0.012 * intensity * (0.5 + 0.5 * sway),
      rotationOffset: Math.sin(phase * 0.22) * 0.35 * intensity,
      opacityMultiplier: clamp(0.9 + 0.12 * intensity, 0.3, 1.2),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "heat-distortion") {
    const shimmer = 0.5 + 0.5 * Math.sin(phase * 2.6);
    return {
      offsetX: Math.sin(phase * 1.7) * 9 * intensity * amplitude * shimmer,
      offsetY: Math.cos(phase * 0.45) * 2 * intensity * amplitude,
      scaleMultiplier: 1 + 0.02 * intensity * shimmer,
      rotationOffset: Math.sin(phase * 0.6) * 0.7 * intensity,
      opacityMultiplier: clamp(0.9 + 0.14 * intensity * shimmer, 0.2, 1.22),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "confetti-burst") {
    const burst = Math.pow(clamp01(1 - Math.abs(packet.progress - 0.2) / 0.24), 1.2);
    return {
      offsetX: Math.sin(seedPhase) * 20 * intensity * amplitude * burst,
      offsetY: -Math.abs(Math.cos(seedPhase)) * 14 * intensity * amplitude * burst,
      scaleMultiplier: 1 + 0.05 * intensity * burst,
      rotationOffset: hashSigned(packet.seed + 62.1) * 2.2 * intensity * burst,
      opacityMultiplier: clamp(0.86 + 0.34 * intensity * burst, 0.24, 1.35),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "fire-sparks") {
    const spark = 0.5 + 0.5 * Math.sin(phase * 2.4);
    return {
      offsetX: Math.sin(phase * 0.7) * 6 * intensity * amplitude,
      offsetY: -Math.abs(Math.cos(phase * 1.05)) * 10 * intensity * amplitude,
      scaleMultiplier: 1 + 0.028 * intensity * spark,
      rotationOffset: Math.sin(phase * 0.5) * 1.2 * intensity,
      opacityMultiplier: clamp(0.88 + 0.26 * intensity * spark, 0.22, 1.26),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "glitch-frame-tear") {
    const tick = Math.floor(playhead * 30 * speed);
    const tearGate = hash01(packet.seed + tick * 4.93 + 2.4) > 0.72 ? 1 : 0;
    const tearX = hashSigned(packet.seed + tick * 10.31);
    const tearY = hashSigned(packet.seed + tick * 7.79 + 5.3);
    const tearRot = hashSigned(packet.seed + tick * 2.21 + 21.9);
    return {
      offsetX: tearX * 20 * intensity * amplitude * tearGate,
      offsetY: tearY * 6 * intensity * amplitude * tearGate,
      scaleMultiplier: 1 + 0.026 * intensity * tearGate,
      rotationOffset: tearRot * 2.1 * intensity * tearGate,
      opacityMultiplier: clamp(0.84 + 0.3 * tearGate, 0.2, 1.24),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "glitch-rgb") {
    const tick = Math.floor(playhead * 24 * speed);
    const glitchGate = hash01(packet.seed + tick * 7.19) > 0.68 ? 1 : 0;
    const jitterX = hashSigned(packet.seed + tick * 13.31);
    const jitterY = hashSigned(packet.seed + tick * 9.23 + 4.1);
    const jitterR = hashSigned(packet.seed + tick * 3.71 + 8.9);
    return {
      offsetX: jitterX * 18 * intensity * amplitude * glitchGate,
      offsetY: jitterY * 10 * intensity * amplitude * glitchGate,
      scaleMultiplier: 1 + 0.028 * intensity * glitchGate,
      rotationOffset: jitterR * 2.6 * intensity * glitchGate,
      opacityMultiplier: clamp(0.82 + 0.34 * glitchGate, 0.24, 1.26),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "ripple-warp") {
    const ripple = Math.sin(phase * 2.8);
    const ring = Math.cos(phase * 1.35);
    return {
      offsetX: ripple * 11 * intensity * amplitude,
      offsetY: ring * 7 * intensity * amplitude,
      scaleMultiplier: 1 + 0.085 * intensity * Math.abs(ripple),
      rotationOffset: ring * 1.5 * intensity,
      opacityMultiplier: clamp(0.9 + 0.2 * intensity * (0.5 + 0.5 * ripple), 0.28, 1.25),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "zoom-punch") {
    const center = 0.16;
    const width = 0.22;
    const envelope = clamp01(1 - Math.abs(packet.progress - center) / width);
    const punch = envelope * envelope * intensity * amplitude;
    const direction = packet.progress <= center ? 1 : -0.45;
    return {
      offsetX: Math.sin(seedPhase) * 11 * punch,
      offsetY: Math.cos(seedPhase) * 6 * punch,
      scaleMultiplier: 1 + 0.24 * punch * direction,
      rotationOffset: hashSigned(packet.seed + 31.1) * 2.1 * punch,
      opacityMultiplier: clamp(0.92 + 0.26 * punch, 0.3, 1.32),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "beat-zoom-micro") {
    const micro = 0.5 + 0.5 * Math.sin(phase * 6.4);
    return {
      offsetX: 0,
      offsetY: Math.sin(phase * 1.1) * 3 * intensity * amplitude * micro,
      scaleMultiplier: 1 + 0.045 * intensity * amplitude * micro,
      rotationOffset: Math.sin(phase * 0.8) * 0.45 * intensity * micro,
      opacityMultiplier: clamp(0.92 + 0.2 * intensity * micro, 0.24, 1.28),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "beat-rgb-pulse") {
    const beatPulse = 0.5 + 0.5 * Math.sin(phase * 4.1);
    return {
      offsetX: Math.sin(phase * 1.8) * 7 * intensity * amplitude * beatPulse,
      offsetY: Math.cos(phase * 1.5) * 4 * intensity * amplitude * beatPulse,
      scaleMultiplier: 1 + 0.06 * intensity * beatPulse,
      rotationOffset: Math.sin(phase * 1.1) * 1.2 * intensity * beatPulse,
      opacityMultiplier: clamp(0.88 + 0.36 * intensity * beatPulse, 0.24, 1.34),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "beat-particle-burst") {
    const burst = 0.5 + 0.5 * Math.sin(phase * 4.8);
    return {
      offsetX: Math.sin(seedPhase + phase * 1.2) * 12 * intensity * amplitude * burst,
      offsetY: Math.cos(seedPhase + phase * 1.1) * 8 * intensity * amplitude * burst,
      scaleMultiplier: 1 + 0.09 * intensity * burst,
      rotationOffset: hashSigned(packet.seed + phase * 9.3) * 1.8 * intensity * burst,
      opacityMultiplier: clamp(0.86 + 0.4 * intensity * burst, 0.22, 1.38),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "beat-light-leak") {
    const leakBeat = 0.5 + 0.5 * Math.sin(phase * 3.7);
    return {
      offsetX: Math.sin(phase * 0.9) * 10 * intensity * amplitude * leakBeat,
      offsetY: Math.cos(phase * 0.65) * 5 * intensity * amplitude * leakBeat,
      scaleMultiplier: 1 + 0.045 * intensity * leakBeat,
      rotationOffset: Math.sin(phase * 0.55) * 1.1 * intensity * leakBeat,
      opacityMultiplier: clamp(0.9 + 0.3 * intensity * leakBeat, 0.24, 1.32),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "beat-outline-flicker") {
    const flick = 0.5 + 0.5 * Math.sin(phase * 6.2);
    return {
      offsetX: Math.sin(phase * 2.1) * 5 * intensity * amplitude * flick,
      offsetY: Math.cos(phase * 1.7) * 3 * intensity * amplitude * flick,
      scaleMultiplier: 1 + 0.04 * intensity * flick,
      rotationOffset: Math.sin(phase * 1.3) * 0.9 * intensity * flick,
      opacityMultiplier: clamp(0.78 + 0.55 * intensity * flick, 0.15, 1.45),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "treble-sparkle") {
    const sparkle = 0.5 + 0.5 * Math.sin(phase * 7.4);
    return {
      offsetX: Math.sin(phase * 2.9) * 4 * intensity * amplitude * sparkle,
      offsetY: Math.cos(phase * 2.4) * 3 * intensity * amplitude * sparkle,
      scaleMultiplier: 1 + 0.05 * intensity * sparkle,
      rotationOffset: Math.sin(phase * 1.8) * 1.1 * intensity * sparkle,
      opacityMultiplier: clamp(0.9 + 0.34 * intensity * sparkle, 0.24, 1.34),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "bass-shake") {
    const bassEnvelope = 0.5 + 0.5 * Math.sin(phase * 2.2);
    const tick = Math.floor(playhead * 30 * speed);
    const bassJitterX = hashSigned(packet.seed + tick * 10.77);
    const bassJitterY = hashSigned(packet.seed + tick * 6.19 + 13.2);
    return {
      offsetX: bassJitterX * 14 * intensity * amplitude * bassEnvelope,
      offsetY: bassJitterY * 10 * intensity * amplitude * bassEnvelope,
      scaleMultiplier: 1 + 0.03 * intensity * bassEnvelope,
      rotationOffset: bassJitterX * 1.4 * intensity * bassEnvelope,
      opacityMultiplier: clamp(0.9 + 0.2 * intensity * bassEnvelope, 0.22, 1.3),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "beat-ripple") {
    const beat = 0.5 + 0.5 * Math.sin(phase * 3.2);
    return {
      offsetX: Math.sin(phase * 1.6) * 6 * intensity * amplitude * beat,
      offsetY: Math.cos(phase * 1.6) * 4 * intensity * amplitude * beat,
      scaleMultiplier: 1 + 0.12 * intensity * amplitude * beat,
      rotationOffset: Math.sin(phase * 0.9) * 1.1 * intensity * beat,
      opacityMultiplier: clamp(0.88 + 0.3 * intensity * beat, 0.28, 1.3),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "bass-zoom") {
    const low = 0.5 + 0.5 * Math.sin(phase * 0.85);
    return {
      offsetX: 0,
      offsetY: Math.sin(phase * 0.7) * 8 * intensity * amplitude,
      scaleMultiplier: 1 + 0.16 * intensity * amplitude * low,
      rotationOffset: Math.sin(phase * 0.42) * 0.95 * intensity,
      opacityMultiplier: clamp(0.9 + 0.24 * intensity * low, 0.28, 1.3),
      globalCompositeOperation: blendComposite,
    };
  }

  // Generic fallback for unknown effect names.
  return {
    offsetX: Math.sin(phase) * 6 * intensity * amplitude,
    offsetY: Math.cos(phase * 0.8) * 3 * intensity * amplitude,
    scaleMultiplier: 1 + 0.025 * intensity * Math.sin(phase * 1.1),
    rotationOffset: Math.sin(phase * 0.6) * 0.8 * intensity,
    opacityMultiplier: clamp(0.92 + 0.16 * Math.sin(phase * 1.9), 0.3, 1.2),
    globalCompositeOperation: blendComposite,
  };
}

function evaluateBodyPacketModifier(args: {
  fxType: string;
  phase: number;
  intensity: number;
  amplitude: number;
  blendComposite?: GlobalCompositeOperation;
  bodyTrackingState?: BodyTrackingState | null;
}): FxNodeModifier {
  const { fxType, phase, intensity, amplitude, blendComposite, bodyTrackingState } = args;
  const tracking = bodyTrackingState ?? null;
  const hasTrackedSubject = !!tracking?.trackedBox;
  const centerX = hasTrackedSubject ? tracking.smoothedTrajectory.x : 0.5;
  const centerY = hasTrackedSubject ? tracking.smoothedTrajectory.y : 0.5;
  const velocityX = tracking?.smoothedTrajectory.vx ?? 0;
  const velocityY = tracking?.smoothedTrajectory.vy ?? 0;
  const confidence = clamp01(tracking?.confidence ?? 0);
  const fallbackDamp = tracking?.deterministicFallback ? 0.72 : 1;
  const followX = clamp((0.5 - centerX) * 2, -1, 1);
  const followY = clamp((0.5 - centerY) * 2, -1, 1);
  const velocityNormX = clamp(velocityX * 8, -1, 1);
  const velocityNormY = clamp(velocityY * 8, -1, 1);
  const velocityMagnitude = clamp(Math.hypot(velocityNormX, velocityNormY), 0, 1);
  const trackingGain = hasTrackedSubject ? clamp(0.45 + confidence * 0.55, 0.35, 1) : 0.35;
  const bodyIntensity = clamp01(intensity * trackingGain) * fallbackDamp;
  const bodyAmp = Math.max(0.25, amplitude);

  if (fxType === "spotlight") {
    return {
      offsetX: followX * 30 * bodyIntensity * bodyAmp,
      offsetY: followY * 16 * bodyIntensity * bodyAmp,
      scaleMultiplier: 1 + 0.05 * bodyIntensity * (1 + 0.4 * confidence),
      rotationOffset: clamp((followX * 1.4) + velocityNormX * 1.8, -2.2, 2.2) * bodyIntensity,
      opacityMultiplier: clamp(1 + 0.12 * bodyIntensity, 0.5, 1.35),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "motion-trail") {
    const trailPulse = 0.5 + 0.5 * Math.sin(phase * 1.7);
    return {
      offsetX: velocityNormX * 22 * bodyIntensity * bodyAmp * (0.6 + trailPulse * 0.4),
      offsetY: velocityNormY * 16 * bodyIntensity * bodyAmp * (0.6 + trailPulse * 0.4),
      scaleMultiplier: 1 + 0.02 * bodyIntensity * velocityMagnitude,
      rotationOffset: velocityNormX * 2.5 * bodyIntensity,
      opacityMultiplier: clamp(0.86 + 0.36 * bodyIntensity * trailPulse, 0.25, 1.25),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "outline-glow" || fxType === "glow-pulse") {
    const pulse = 0.5 + 0.5 * Math.sin(phase * 2.2);
    return {
      offsetX: followX * 8 * bodyIntensity,
      offsetY: followY * 5 * bodyIntensity,
      scaleMultiplier: 1 + 0.03 * bodyIntensity * pulse,
      rotationOffset: velocityNormX * 0.8 * bodyIntensity,
      opacityMultiplier: clamp(0.92 + 0.42 * bodyIntensity * pulse * (0.5 + confidence * 0.5), 0.3, 1.35),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "energy-aura") {
    const auraPulse = 0.5 + 0.5 * Math.sin(phase * 2.6);
    const velocityBoost = 0.7 + velocityMagnitude * 0.6;
    return {
      offsetX: followX * 12 * bodyIntensity * velocityBoost + velocityNormX * 8 * bodyIntensity,
      offsetY: followY * 9 * bodyIntensity * velocityBoost + velocityNormY * 6 * bodyIntensity,
      scaleMultiplier: 1 + 0.06 * bodyIntensity * auraPulse * (0.6 + confidence * 0.4),
      rotationOffset: (velocityNormX * 1.3 + followX * 0.8) * bodyIntensity,
      opacityMultiplier: clamp(0.88 + 0.42 * bodyIntensity * auraPulse, 0.24, 1.38),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "edge-neon-pulse") {
    const neonPulse = 0.5 + 0.5 * Math.sin(phase * 3.1);
    return {
      offsetX: followX * 6 * bodyIntensity + velocityNormX * 4 * bodyIntensity,
      offsetY: followY * 4 * bodyIntensity + velocityNormY * 3 * bodyIntensity,
      scaleMultiplier: 1 + 0.034 * bodyIntensity * neonPulse,
      rotationOffset: velocityNormX * 0.9 * bodyIntensity,
      opacityMultiplier: clamp(0.92 + 0.48 * bodyIntensity * neonPulse, 0.28, 1.42),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "shadow-echo") {
    return {
      offsetX: -velocityNormX * 18 * bodyIntensity * bodyAmp,
      offsetY: -velocityNormY * 14 * bodyIntensity * bodyAmp,
      scaleMultiplier: 1 + 0.015 * bodyIntensity * (1 - velocityMagnitude * 0.4),
      rotationOffset: -velocityNormX * 1.4 * bodyIntensity,
      opacityMultiplier: clamp(0.78 + 0.25 * bodyIntensity, 0.2, 1.1),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "subject-pulse-warp") {
    const warp = 0.5 + 0.5 * Math.sin(phase * 2.4);
    const directionX = velocityNormX === 0 ? Math.sign(followX || 1) : Math.sign(velocityNormX);
    return {
      offsetX: directionX * 10 * bodyIntensity * bodyAmp * warp,
      offsetY: followY * 5 * bodyIntensity * bodyAmp * warp,
      scaleMultiplier: 1 + 0.08 * bodyIntensity * warp,
      rotationOffset: directionX * 1.1 * bodyIntensity * warp,
      opacityMultiplier: clamp(0.9 + 0.3 * bodyIntensity * warp, 0.24, 1.34),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "background-blur-only" || fxType === "background-blur") {
    if (hasTrackedSubject) {
      // Runtime split path handles background treatment; keep base subject transform neutral.
      return {
        offsetX: 0,
        offsetY: 0,
        scaleMultiplier: 1,
        rotationOffset: 0,
        opacityMultiplier: 1,
        globalCompositeOperation: blendComposite,
      };
    }
    const suppress = clamp(1 - confidence * 0.35, 0.62, 1);
    return {
      offsetX: followX * 4 * bodyIntensity,
      offsetY: followY * 3 * bodyIntensity,
      scaleMultiplier: 1 + 0.015 * bodyIntensity,
      rotationOffset: 0,
      opacityMultiplier: clamp(suppress + 0.1 * bodyIntensity, 0.5, 1.15),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "phantom-clone") {
    const phasePulse = Math.sin(phase * 1.9);
    return {
      offsetX: followX * 14 * bodyIntensity + velocityNormX * 10 * bodyIntensity,
      offsetY: followY * 9 * bodyIntensity + velocityNormY * 7 * bodyIntensity,
      scaleMultiplier: 1 + 0.045 * bodyIntensity * (0.5 + 0.5 * phasePulse),
      rotationOffset: clamp((velocityNormX + followX) * 2.6, -3, 3) * bodyIntensity,
      opacityMultiplier: clamp(0.82 + 0.38 * bodyIntensity * (0.5 + 0.5 * phasePulse), 0.24, 1.3),
      globalCompositeOperation: blendComposite,
    };
  }

  if (fxType === "subject-freeze-frame-highlight") {
    const freezeWindow = clamp01(1 - Math.abs(Math.sin(phase * 0.5)) * 1.35);
    const freezeGain = Math.max(freezeWindow, 0.12);
    const dampVelocity = 1 - velocityMagnitude * 0.8;
    return {
      offsetX: followX * 4 * bodyIntensity * freezeGain * dampVelocity,
      offsetY: followY * 3 * bodyIntensity * freezeGain * dampVelocity,
      scaleMultiplier: 1 + 0.05 * bodyIntensity * freezeGain,
      rotationOffset: followX * 0.55 * bodyIntensity * freezeGain,
      opacityMultiplier: clamp(0.94 + 0.28 * bodyIntensity * freezeGain, 0.3, 1.32),
      globalCompositeOperation: blendComposite,
    };
  }

  // Body-safe fallback when effect type is unknown or tracking is unavailable.
  return {
    offsetX: followX * 8 * bodyIntensity,
    offsetY: followY * 5 * bodyIntensity,
    scaleMultiplier: 1 + 0.02 * bodyIntensity,
    rotationOffset: velocityNormX * 1.2 * bodyIntensity,
    opacityMultiplier: clamp(0.88 + 0.2 * bodyIntensity * (1 + 0.4 * velocityMagnitude), 0.28, 1.2),
    globalCompositeOperation: blendComposite,
  };
}

// resolveModulationFactor through maybePruneStaleModulationState moved
// to fxRuntime.evaluation.ts (T7 of the file-splits plan).

function setNodeCompositeOperation(node: Konva.Node, next: GlobalCompositeOperation): boolean {
  const api = node as any;
  if (typeof api.globalCompositeOperation !== "function") return false;
  const current = api.globalCompositeOperation();
  if (current === next) return false;
  api.globalCompositeOperation(next);
  return true;
}

function getNodeCompositeOperation(node: Konva.Node): GlobalCompositeOperation {
  const api = node as any;
  if (typeof api.globalCompositeOperation !== "function") return "source-over";
  const current = api.globalCompositeOperation();
  if (typeof current !== "string" || current.length === 0) return "source-over";
  return current as GlobalCompositeOperation;
}

function assignIfChanged(current: number, next: number, setter: (value: number) => void): boolean {
  if (Math.abs(current - next) <= FX_EPSILON) return false;
  setter(next);
  return true;
}

// toFinite, hash01, hashSigned moved to fxRuntime.shared.ts (T7).

function resolveMaskRect(args: {
  params: Record<string, number>;
  width: number;
  height: number;
  expandPx: number;
}): { x: number; y: number; width: number; height: number } {
  const { params, width, height, expandPx } = args;
  const nx = resolveMaskUnitValue(params.maskX, 0, width);
  const ny = resolveMaskUnitValue(params.maskY, 0, height);
  const nw = resolveMaskUnitValue(params.maskWidth, width, width);
  const nh = resolveMaskUnitValue(params.maskHeight, height, height);

  const x = clamp(nx - expandPx, 0, width - 1);
  const y = clamp(ny - expandPx, 0, height - 1);
  const maxW = Math.max(1, width - x);
  const maxH = Math.max(1, height - y);
  const w = clamp(nw + expandPx * 2, 1, maxW);
  const h = clamp(nh + expandPx * 2, 1, maxH);
  return { x, y, width: w, height: h };
}

function resolveMaskUnitValue(value: unknown, fallbackPx: number, unitPx: number): number {
  const n = toFinite(value, fallbackPx);
  if (!Number.isFinite(n)) return fallbackPx;
  if (Math.abs(n) <= 1) return n * unitPx;
  return n;
}

function drawRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  reverse: boolean
): void {
  const x2 = x + width;
  const y2 = y + height;
  if (!reverse) {
    ctx.moveTo(x, y);
    ctx.lineTo(x2, y);
    ctx.lineTo(x2, y2);
    ctx.lineTo(x, y2);
  } else {
    ctx.moveTo(x, y);
    ctx.lineTo(x, y2);
    ctx.lineTo(x2, y2);
    ctx.lineTo(x2, y);
  }
  ctx.closePath();
}

function drawEllipsePath(
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
  reverse: boolean
): void {
  const rx = Math.max(0.5, rect.width / 2);
  const ry = Math.max(0.5, rect.height / 2);
  const cx = rect.x + rx;
  const cy = rect.y + ry;
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2, reverse);
}

function drawLinearGradientMaskPath(
  ctx: CanvasRenderingContext2D,
  params: Record<string, number>,
  width: number,
  height: number,
  reverse: boolean
): void {
  const angle = ((toFinite(params.maskAngleDeg, 0) % 360) + 360) % 360;
  const stop = clamp01(toFinite(params.maskStop, 0.65));

  const isHorizontal = angle < 45 || angle >= 315 || (angle >= 135 && angle < 225);
  if (isHorizontal) {
    const leftToRight = angle < 45 || angle >= 315;
    const segment = clamp(width * stop, 1, width);
    if (leftToRight) {
      drawRectPath(ctx, 0, 0, segment, height, reverse);
    } else {
      drawRectPath(ctx, width - segment, 0, segment, height, reverse);
    }
    return;
  }

  const topToBottom = angle >= 45 && angle < 135;
  const segment = clamp(height * stop, 1, height);
  if (topToBottom) {
    drawRectPath(ctx, 0, 0, width, segment, reverse);
  } else {
    drawRectPath(ctx, 0, height - segment, width, segment, reverse);
  }
}

function drawRadialMaskPath(
  ctx: CanvasRenderingContext2D,
  params: Record<string, number>,
  rect: { x: number; y: number; width: number; height: number },
  width: number,
  height: number,
  reverse: boolean
): void {
  const cx = resolveMaskUnitValue(params.maskCenterX, rect.x + rect.width / 2, width);
  const cy = resolveMaskUnitValue(params.maskCenterY, rect.y + rect.height / 2, height);
  const radiusRaw = resolveMaskUnitValue(params.maskRadius, Math.min(rect.width, rect.height) / 2, Math.min(width, height));
  const radius = clamp(radiusRaw, 1, Math.max(width, height));
  ctx.arc(cx, cy, radius, 0, Math.PI * 2, reverse);
}

type FxFreeDrawPoint = {
  x: number;
  y: number;
  cpIn?: { x: number; y: number };
  cpOut?: { x: number; y: number };
};

function drawFreeDrawMaskPath(
  ctx: CanvasRenderingContext2D,
  args: {
    mask: FxBlock["mask"];
    width: number;
    height: number;
    reverse: boolean;
  }
): boolean {
  const { mask, width, height, reverse } = args;
  const raw = Array.isArray(mask.paths) ? mask.paths : [];
  if (raw.length === 0) return false;

  const usable = raw.filter((path) => Array.isArray(path.points) && path.points.length >= 2);
  if (usable.length === 0) return false;

  const startIdx = findLastReplacePathIndex(usable);
  const orderedPaths = startIdx >= 0 ? usable.slice(startIdx) : usable;
  const expandPx = clamp(toFinite(mask.expandPx, 0), -500, 500);

  for (const path of orderedPaths) {
    const op = path.operation ?? "add";
    const shouldReverse = op === "subtract" ? !reverse : reverse;
    drawFreeDrawPathSegment(ctx, path, width, height, shouldReverse, expandPx);
  }

  return true;
}

function findLastReplacePathIndex(
  paths: Array<{ operation?: "replace" | "add" | "subtract" | "intersect" }>
): number {
  for (let i = paths.length - 1; i >= 0; i -= 1) {
    if (paths[i].operation === "replace") return i;
  }
  return -1;
}

function drawFreeDrawPathSegment(
  ctx: CanvasRenderingContext2D,
  path: {
    points: Array<{
      x: number;
      y: number;
      cpIn?: { x: number; y: number };
      cpOut?: { x: number; y: number };
    }>;
    closed?: boolean;
  },
  width: number,
  height: number,
  reverse: boolean,
  expandPx: number
): void {
  const resolved = path.points.map((point) => resolveFreeDrawPoint(point, width, height));
  if (resolved.length < 2) return;

  const expanded = applyFreeDrawExpandPx(resolved, expandPx);
  const points = reverse
    ? expanded
        .slice()
        .reverse()
        .map((point) => ({
          x: point.x,
          y: point.y,
          cpIn: point.cpOut ? { ...point.cpOut } : undefined,
          cpOut: point.cpIn ? { ...point.cpIn } : undefined,
        }))
    : expanded;
  if (points.length < 2) return;

  const first = points[0];
  ctx.moveTo(first.x, first.y);

  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const next = points[i];
    const cp1 = prev.cpOut;
    const cp2 = next.cpIn;
    if (cp1 || cp2) {
      ctx.bezierCurveTo(
        cp1?.x ?? prev.x,
        cp1?.y ?? prev.y,
        cp2?.x ?? next.x,
        cp2?.y ?? next.y,
        next.x,
        next.y
      );
    } else {
      ctx.lineTo(next.x, next.y);
    }
  }

  const closed = path.closed !== false;
  if (closed && points.length > 2) {
    const last = points[points.length - 1];
    const cp1 = last.cpOut;
    const cp2 = first.cpIn;
    if (cp1 || cp2) {
      ctx.bezierCurveTo(
        cp1?.x ?? last.x,
        cp1?.y ?? last.y,
        cp2?.x ?? first.x,
        cp2?.y ?? first.y,
        first.x,
        first.y
      );
    }
    ctx.closePath();
    return;
  }

  // Clip paths must enclose area; force closure for open paths.
  ctx.lineTo(first.x, first.y);
  ctx.closePath();
}

function resolveFreeDrawPoint(
  point: {
    x: number;
    y: number;
    cpIn?: { x: number; y: number };
    cpOut?: { x: number; y: number };
  },
  width: number,
  height: number
): FxFreeDrawPoint {
  const x = resolveMaskUnitValue(point.x, 0, width);
  const y = resolveMaskUnitValue(point.y, 0, height);
  const cpIn = point.cpIn
    ? {
        x: resolveMaskUnitValue(point.cpIn.x, x, width),
        y: resolveMaskUnitValue(point.cpIn.y, y, height),
      }
    : undefined;
  const cpOut = point.cpOut
    ? {
        x: resolveMaskUnitValue(point.cpOut.x, x, width),
        y: resolveMaskUnitValue(point.cpOut.y, y, height),
      }
    : undefined;

  return { x, y, cpIn, cpOut };
}

function applyFreeDrawExpandPx(points: FxFreeDrawPoint[], expandPx: number): FxFreeDrawPoint[] {
  if (points.length < 2) return points;
  if (Math.abs(expandPx) <= 0.001) return points;

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    minX = Math.min(minX, point.x, point.cpIn?.x ?? point.x, point.cpOut?.x ?? point.x);
    minY = Math.min(minY, point.y, point.cpIn?.y ?? point.y, point.cpOut?.y ?? point.y);
    maxX = Math.max(maxX, point.x, point.cpIn?.x ?? point.x, point.cpOut?.x ?? point.x);
    maxY = Math.max(maxY, point.y, point.cpIn?.y ?? point.y, point.cpOut?.y ?? point.y);
  }

  const width = Math.max(1, maxX - minX);
  const height = Math.max(1, maxY - minY);
  const base = Math.max(1, Math.min(width, height));
  const scale = clamp(1 + expandPx / base, 0.05, 25);
  if (Math.abs(scale - 1) <= 0.0001) return points;

  const cx = minX + width / 2;
  const cy = minY + height / 2;
  return points.map((point) => ({
    x: cx + (point.x - cx) * scale,
    y: cy + (point.y - cy) * scale,
    cpIn: point.cpIn
      ? {
          x: cx + (point.cpIn.x - cx) * scale,
          y: cy + (point.cpIn.y - cy) * scale,
        }
      : undefined,
    cpOut: point.cpOut
      ? {
          x: cx + (point.cpOut.x - cx) * scale,
          y: cy + (point.cpOut.y - cy) * scale,
        }
      : undefined,
  }));
}

// evaluateFxBlockAtPlayhead, inferFxKeyframeTimeMode, sampleLinear,
// applyEvaluatedFxProperty, clamp moved to fxRuntime.evaluation.ts /
// fxRuntime.shared.ts (T7 of the file-splits plan).
